import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import invoiceService, { InvoiceFilters, CreateInvoiceDto, InvoiceStats } from '../../services/invoiceService';
import { Invoice, Payment } from '../../types';

const errorMessage = (e: unknown): string =>
  (e as AxiosError<{ message?: string }>).response?.data?.message ?? (e as Error).message;

interface InvoiceState {
  list:     Invoice[];
  stats:    InvoiceStats | null;
  selected: Invoice | null;
  loading:  boolean;
  error:    string | null;
  total:    number;
}

const initialState: InvoiceState = {
  list: [], stats: null, selected: null,
  loading: false, error: null, total: 0,
};

const upsert = (list: Invoice[], inv: Invoice) => {
  const i = list.findIndex(x => x._id === inv._id);
  if (i !== -1) list[i] = inv; else list.unshift(inv);
};

export const fetchInvoices     = createAsyncThunk('invoices/fetchAll',   async (f: InvoiceFilters, { rejectWithValue }) => { try { return await invoiceService.getAll(f); } catch (e: unknown) { return rejectWithValue(errorMessage(e)); } });
export const fetchInvoiceStats = createAsyncThunk('invoices/fetchStats', async (_, { rejectWithValue }) => { try { return await invoiceService.getStats(); } catch (e: unknown) { return rejectWithValue(errorMessage(e)); } });
export const fetchInvoiceById  = createAsyncThunk('invoices/fetchById',  async (id: string, { rejectWithValue }) => { try { return await invoiceService.getById(id); } catch (e: unknown) { return rejectWithValue(errorMessage(e)); } });
export const createInvoice     = createAsyncThunk('invoices/create',     async (d: CreateInvoiceDto, { rejectWithValue }) => { try { return await invoiceService.create(d); } catch (e: unknown) { return rejectWithValue(errorMessage(e)); } });
export const updateInvoice     = createAsyncThunk('invoices/update',     async ({id,data}:{id:string;data:{invoiceDate?:string;notes?:string;clientId?:string;items?:CreateInvoiceDto['items']}}, { rejectWithValue }) => { try { return await invoiceService.update(id,data); } catch (e: unknown) { return rejectWithValue(errorMessage(e)); } });
export const processPayment    = createAsyncThunk('invoices/payment',    async ({ id, payment }: { id: string; payment: Omit<Payment,'timestamp'> }, { rejectWithValue }) => { try { return await invoiceService.processPayment(id, payment); } catch (e: unknown) { return rejectWithValue(errorMessage(e)); } });
export const cancelInvoice     = createAsyncThunk('invoices/cancel',     async ({ id, reason }: { id: string; reason?: string }, { rejectWithValue }) => { try { return await invoiceService.cancel(id, reason); } catch (e: unknown) { return rejectWithValue(errorMessage(e)); } });
export const refundInvoice     = createAsyncThunk('invoices/refund',     async ({id,...data}:{id:string;reason?:string;refundMode?:'original'|'cash'|'upi';reference?:string}, { rejectWithValue }) => { try { return await invoiceService.refund(id,data); } catch (e: unknown) { return rejectWithValue(errorMessage(e)); } });

const invoiceSlice = createSlice({
  name: 'invoices', initialState,
  reducers: {
    setSelected: (state, action: PayloadAction<Invoice | null>) => { state.selected = action.payload; },
    clearError:  (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending,      (s) => { s.loading = true; s.error = null; })
      .addCase(fetchInvoices.fulfilled,    (s, a) => {
        s.loading = false;
        s.list = a.payload.data ?? [];
        s.total = a.payload.total ?? 0;
        if (s.selected) {
          const populatedSelected = s.list.find(invoice => invoice._id === s.selected?._id);
          if (populatedSelected) s.selected = populatedSelected;
        }
      })
      .addCase(fetchInvoices.rejected,     (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(fetchInvoiceStats.fulfilled,(s, a) => { if (a.payload.data) s.stats = a.payload.data; })
      .addCase(fetchInvoiceById.fulfilled, (s, a) => { if (a.payload.data) { upsert(s.list, a.payload.data); s.selected = a.payload.data; } })
      .addCase(createInvoice.fulfilled,    (s, a) => { if (a.payload.data) { upsert(s.list, a.payload.data); s.selected = a.payload.data; } })
      .addCase(updateInvoice.fulfilled,    (s, a) => { if (a.payload.data) { upsert(s.list, a.payload.data); s.selected = a.payload.data; } })
      .addCase(processPayment.fulfilled,   (s, a) => { if (a.payload.data) { upsert(s.list, a.payload.data); if (s.selected?._id === a.payload.data._id) s.selected = a.payload.data; } })
      .addCase(cancelInvoice.fulfilled,    (s, a) => { if (a.payload.data) { upsert(s.list, a.payload.data); if (s.selected?._id === a.payload.data._id) s.selected = a.payload.data; } })
      .addCase(refundInvoice.fulfilled,    (s, a) => { if (a.payload.data) { upsert(s.list, a.payload.data); if (s.selected?._id === a.payload.data._id) s.selected = a.payload.data; } });
  },
});

export const { setSelected, clearError } = invoiceSlice.actions;
export default invoiceSlice.reducer;
